import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { logEvent } from '../../lib/analytics';
import { useVoiceDemo } from '../../providers/VoiceDemoContext';
import { useFeatureFlag } from '../../lib/useFeatureFlags';

const EDITOR_STYLES =
  'h-[150px] @sm:h-[250px] overflow-y-auto text-left text-[1rem] @sm:text-xl font-[400] leading-tight outline-none w-full';
export const MAX_EDITOR_CONTENT_LENGTH = 500;
export function Editor() {
  const {
    currentContent: content,
    handleUpdateTextContent: onTextContentChange,
    numGeneration,
  } = useVoiceDemo();
  const { variant: audioGenerationLimit } = useFeatureFlag(
    'vo_demo_generationLimit',
    -1,
  );
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const [localContent, setLocalContent] = useState(content);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    setLocalContent(content);
  }, [content]);

  const calculateEndPosition = () => {
    if (editorRef.current) {
      const textarea = editorRef.current;
      const text = textarea.value;

      const div = document.createElement('span');
      div.style.position = 'absolute';
      div.style.visibility = 'hidden';
      div.style.whiteSpace = 'pre-wrap';
      div.style.wordWrap = 'break-word';
      div.style.width = `${textarea.offsetWidth}px`;
      div.style.font = window.getComputedStyle(textarea).font;
      div.style.lineHeight = window.getComputedStyle(textarea).lineHeight;
      const lh = parseFloat(window.getComputedStyle(textarea).lineHeight);
      div.textContent = text;
      const span = document.createElement('span');
      div.appendChild(span);
      document.body.appendChild(div);

      const divRect = div.getBoundingClientRect();
      const rect = span.getBoundingClientRect();
      const top = divRect.height - lh;
      const left = rect.left;
      setCursorPosition({ top, left });
      document.body.removeChild(div);
    }
  };

  const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    calculateEndPosition();
  }, [localContent]);

  useEffect(() => {
    if (editorRef.current && window.screen.width > 800) {
      const textLength = editorRef.current.value.length;
      editorRef.current.setSelectionRange(textLength, textLength);
      editorRef.current.focus();
    }
  }, []);

  return (
    <div className="relative">
      <textarea
        className={clsx('resize-none', EDITOR_STYLES)}
        ref={editorRef}
        spellCheck="false"
        aria-label="Text to convert to speech"
        value={localContent}
        onChange={(e) => setLocalContent(e.target.value)}
        onBlur={() => {
          if (localContent !== content) {
            onTextContentChange(localContent.trim());
            logEvent('editor_interaction', { action: 'manual_text_edit' });
          }
        }}
        onFocus={() => setShowCursor(false)}
      />

      {showCursor && (
        <div
          className={`blinking-cursor h-[1.25rem] @sm:h-[1.5rem]`}
          style={{
            top: cursorPosition.top,
            left: cursorPosition.left,
          }}
        />
      )}
      <div
        className={clsx('absolute bottom-[-16px] flex text-[#747580]', {
          'text-[red]': localContent.trim().length > MAX_EDITOR_CONTENT_LENGTH,
          'right-0': audioGenerationLimit === -1,
          'left-0': audioGenerationLimit !== -1,
          '@sm:bottom-[-56px]': audioGenerationLimit === -1,
        })}>
        {localContent.trim().length}/{MAX_EDITOR_CONTENT_LENGTH}
      </div>
      <div
        className={clsx('absolute bottom-[-16px] right-0 text-[#747580]', {
          hidden: audioGenerationLimit === -1,
        })}>
        <span
          className={clsx({
            'text-[red]': audioGenerationLimit - numGeneration <= 0,
          })}>
          {Math.max(audioGenerationLimit - numGeneration, 0)}
        </span>
        /{audioGenerationLimit} Generations Left
      </div>
    </div>
  );
}
